import { Component, OnInit, ViewChild, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { RecaptchaComponent } from 'ng-recaptcha';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { WebSocketService, WebSocketMessage } from '../../services/websocket.service';
import { AuthService } from '../../services/auth.service';
import { Subscription } from 'rxjs';

// Interfaces para tipado seguro
interface CVUploadResponse {
  success: boolean;
  message: string;
  userId?: string;
  analysisResult?: any;
  s3Key?: string;
  requiresLogin?: boolean;
  status?: string;
  finalStatus?: string;
  analisis_fallido?: boolean;
  processSteps?: {
    step1_upload: string;
    step2_storage: string;
    step3_analysis: string;
    step4_finalization: string;
  };
}

interface ProcessSteps {
  step1_upload: string;
  step2_storage: string;
  step3_analysis: string;
  step4_finalization: string;
}

@Component({
  selector: 'app-para-candidatos',
  templateUrl: './para-candidatos.component.html',
  styleUrls: ['./para-candidatos.component.css']
})
export class ParaCandidatosComponent implements OnInit {
  @ViewChild('recaptcha', { static: false }) recaptchaComponent!: RecaptchaComponent;

  // Formularios
  registrationForm!: FormGroup;
  confirmationForm!: FormGroup;

  // Estados del proceso
  currentStep: number = 1;
  isSubmitting: boolean = false;
  isConfirming: boolean = false;
  isResending: boolean = false;
  isUploading: boolean = false;
  registrationSuccess: boolean = false;

  // Propiedades para el archivo
  selectedFile: File | null = null;
  fileName: string = '';

  // Estado del proceso de CV
  cvUploadStatus = {
    registration: false,
    upload: false,
    storage: false, // Nuevo estado para almacenamiento
    analysis: false,
    completed: false,
    analysisFailed: false
  };

  // Resultado del análisis
  uploadResult: CVUploadResponse | null = null;
  analysisError: string | null = null;

  // Propiedades para el resultado del análisis
  analysisCompleted: boolean = false;
  analysisFailed: boolean = false;
  analysisResult: any = null;

  // Sistema de polling para estados


  // WebSocket integration
  private websocketSubscription: Subscription | null = null;
  websocketStatus = {
    connected: false,
    connecting: false,
    error: null
  };

  // CV Status tracking
  currentCVStatus: {
    status: 'idle' | 'processing' | 'completed' | 'failed';
    step: string;
    progress: number;
  } = {
    status: 'idle',
    step: '',
    progress: 0
  };

  // Timeout handling para cuando no hay respuesta
  private processTimeout: any = null;
  private readonly PROCESS_TIMEOUT_MS = 120000; // 2 minutos

  // Configuración reCAPTCHA
  recaptchaSiteKey = environment.recaptcha.siteKey;
  recaptchaToken: string | null = null;
  isExecutingRecaptcha: boolean = false;
  
  // Token de autenticación
  authToken: string | null = null;
  
  // Control para mostrar opción de login
  showLoginOption: boolean = false;

  // Datos de ejemplo para oportunidades y formación
  ofertasTrabajo = [
    {
      titulo: 'Desarrollador Full Stack',
      empresa: 'TechCorp',
      ubicacion: 'Bogotá, Colombia',
      salario: '$3,000 - $4,500 USD',
      tipo: 'Tiempo completo'
    },
    {
      titulo: 'Analista de Datos',
      empresa: 'DataSolutions',
      ubicacion: 'Medellín, Colombia',
      salario: '$2,800 - $3,800 USD',
      tipo: 'Tiempo completo'
    },
    {
      titulo: 'UX/UI Designer',
      empresa: 'Creative Studio',
      ubicacion: 'Cali, Colombia',
      salario: '$2,500 - $3,500 USD',
      tipo: 'Contrato'
    }
  ];
  
  rutasFormacion = [
    {
      rol: 'Desarrollador Full Stack',
      descripcion: 'Programa completo de desarrollo web con tecnologías modernas',
      duracion: '6 meses',
      modalidad: 'Híbrida'
    },
    {
      rol: 'Analista de Datos',
      descripcion: 'Formación en análisis de datos y machine learning',
      duracion: '4 meses',
      modalidad: 'Online'
    },
    {
      rol: 'DevOps Engineer',
      descripcion: 'Especialización en infraestructura y automatización',
      duracion: '5 meses',
      modalidad: 'Presencial'
    }
  ];
  
  faq = [
    {
      pregunta: '¿Cómo funciona el análisis de CV?',
      respuesta: 'Utilizamos inteligencia artificial avanzada para analizar tu CV y extraer habilidades, experiencia y recomendaciones personalizadas.',
      expanded: false
    },
    {
      pregunta: '¿Cuánto tiempo toma el análisis?',
      respuesta: 'El análisis completo toma entre 2-5 minutos dependiendo de la complejidad del CV.',
      expanded: false
    },
    {
      pregunta: '¿Mis datos están seguros?',
      respuesta: 'Sí, utilizamos encriptación de nivel bancario y cumplimos con todas las regulaciones de protección de datos.',
      expanded: false
    },
    {
      pregunta: '¿Puedo actualizar mi CV más tarde?',
      respuesta: 'Sí, puedes actualizar tu CV en cualquier momento desde tu perfil de usuario.',
      expanded: false
    },
    {
      pregunta: '¿Cómo me notifican las oportunidades?',
      respuesta: 'Te enviaremos notificaciones por email y también puedes revisar tu dashboard personal.',
      expanded: false
    }
  ];

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private authService: AuthService,
    private websocketService: WebSocketService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeForms();
    // Scroll al inicio cuando se carga la página
    this.scrollToTop();
    // La conexión WebSocket se establecerá después de la autenticación exitosa
  }

  ngOnDestroy(): void {
    this.cleanupWebSocket();
    
    // Limpiar timeout si existe
    if (this.processTimeout) {
      clearTimeout(this.processTimeout);
      this.processTimeout = null;
    }
  }

  private initializeForms(): void {
    // Formulario de registro
    this.registrationForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      nombre: ['', [Validators.required, Validators.minLength(2)]],
      apellido: ['', [Validators.required, Validators.minLength(2)]],
      telefono: ['', [Validators.required, Validators.pattern(/^[\d\s\-]{10,}$/), Validators.minLength(10), Validators.maxLength(10)]],
      experiencia: ['', [Validators.required]],
      expectativaSalarial: ['', [Validators.required]],
      modalidadTrabajo: ['', [Validators.required]],
      disponibilidad: ['', [Validators.required]],
      discapacidad: ['', [Validators.required]],
      otraDiscapacidad: [''],
      comentarios: [''],
      consentimientoDatos: [false, [Validators.requiredTrue]]
    });

    // Formulario de confirmación
    this.confirmationForm = this.fb.group({
      confirmationCode: ['', [Validators.required, Validators.minLength(6)]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });

    // Escuchar cambios en discapacidad para mostrar/ocultar campo adicional
    this.registrationForm.get('discapacidad')?.valueChanges.subscribe(value => {
      const otraDiscapacidadControl = this.registrationForm.get('otraDiscapacidad');
      if (value === 'Otra') {
        otraDiscapacidadControl?.setValidators([Validators.required]);
      } else {
        otraDiscapacidadControl?.clearValidators();
        otraDiscapacidadControl?.setValue('');
      }
      otraDiscapacidadControl?.updateValueAndValidity();
    });
  }

  private passwordMatchValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const password = control.get('password');
    const confirmPassword = control.get('confirmPassword');
    
    if (password && confirmPassword && password.value !== confirmPassword.value) {
      return { 'passwordMismatch': true };
    }
    return null;
  }

  private async initializeWebSocket(): Promise<void> {
    try {
      await this.websocketService.connect();
      // Una vez conectado, suscribirse a los mensajes
      this.websocketSubscription = this.websocketService.messages$.subscribe(
        (messages: WebSocketMessage[]) => {
          if (messages.length > 0) {
            const lastMessage = messages[messages.length - 1];
            this.handleWebSocketMessage(lastMessage);
          }
        }
      );
    } catch (error) {
          this.websocketStatus.error = error.message;
    }
  }



  // Métodos para obtener controles de formulario de forma segura
  getEmailControl(): AbstractControl | null {
    return this.registrationForm.get('email');
  }

  getNombreControl(): AbstractControl | null {
    return this.registrationForm.get('nombre');
  }

  getApellidoControl(): AbstractControl | null {
    return this.registrationForm.get('apellido');
  }

  getTelefonoControl(): AbstractControl | null {
    return this.registrationForm.get('telefono');
  }

  getExperienciaControl(): AbstractControl | null {
    return this.registrationForm.get('experiencia');
  }

  getExpectativaSalarialControl(): AbstractControl | null {
    return this.registrationForm.get('expectativaSalarial');
  }

  getModalidadTrabajoControl(): AbstractControl | null {
    return this.registrationForm.get('modalidadTrabajo');
  }

  getDisponibilidadControl(): AbstractControl | null {
    return this.registrationForm.get('disponibilidad');
  }

  getDiscapacidadControl(): AbstractControl | null {
    return this.registrationForm.get('discapacidad');
  }

  getOtraDiscapacidadControl(): AbstractControl | null {
    return this.registrationForm.get('otraDiscapacidad');
  }

  getConfirmationCodeControl(): AbstractControl | null {
    return this.confirmationForm.get('confirmationCode');
  }

  getPasswordControl(): AbstractControl | null {
    return this.confirmationForm.get('password');
  }

  getConfirmPasswordControl(): AbstractControl | null {
    return this.confirmationForm.get('confirmPassword');
  }

  getConsentimientoDatosControl(): AbstractControl | null {
    return this.registrationForm.get('consentimientoDatos');
  }

  // Métodos para obtener valores de forma segura
  getEmailValue(): string {
    return this.registrationForm.get('email')?.value || '';
  }

  getDiscapacidadValue(): string {
    return this.registrationForm.get('discapacidad')?.value || '';
  }

  // Validaciones del formulario
  canSubmitForm(): boolean {
    const formValid = this.registrationForm.valid;
    const fileSelected = !!this.selectedFile;
    const discapacidadValid = this.getDiscapacidadValue() !== 'Otra' || 
                             (this.getOtraDiscapacidadControl()?.value && this.getOtraDiscapacidadControl()?.valid);
    
    // En el paso 1 validamos formulario básico + archivo CV
    return formValid && fileSelected && discapacidadValid;
  }

  isReadyToClickSubmit(): boolean {
    return this.canSubmitForm();
  }

  getSubmitButtonTooltip(): string {
    if (!this.registrationForm.valid) {
      return 'Completa todos los campos obligatorios';
    }
    if (!this.selectedFile) {
      return 'Selecciona un archivo CV';
    }
    if (this.getDiscapacidadValue() === 'Otra' && !this.getOtraDiscapacidadControl()?.value) {
      return 'Especifica el tipo de discapacidad';
    }
    if (!this.getConsentimientoDatosControl()?.value) {
      return 'Debes aceptar el consentimiento para el tratamiento de datos';
    }
    return 'Formulario listo para enviar';
  }

  // Manejo de archivos
  triggerFileSelection(): void {
    const fileInput = document.getElementById('cvFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  // Selección de archivo
  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Validar tipo de archivo
      if (this.isValidFileType(file)) {
        // Validación adicional: verificar extensión del archivo
        if (!file.name.toLowerCase().endsWith('.pdf')) {
          this.selectedFile = null;
          this.fileName = '';
          this.showNotification('error', '❌ Solo se permiten archivos PDF (.pdf)');
          return;
        }
        
        // Validar tamaño del archivo (máximo 10MB)
        const maxSizeMB = 10;
        const maxSizeBytes = maxSizeMB * 1024 * 1024;
        
        if (file.size > maxSizeBytes) {
          this.selectedFile = null;
          this.fileName = '';
          this.showNotification('error', `❌ El archivo es muy grande. Máximo permitido: ${maxSizeMB}MB`);
          return;
        }
        
        this.selectedFile = file;
        this.fileName = file.name;
        this.showNotification('success', `📁 Archivo PDF seleccionado: ${file.name}`);
      } else {
        this.selectedFile = null;
        this.fileName = '';
        this.showNotification('error', '❌ Solo se permiten archivos PDF (.pdf)');
      }
    }
  }

  removeSelectedFile(): void {
    this.selectedFile = null;
    const fileInput = document.getElementById('cvFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  // reCAPTCHA
  onRecaptchaResolved(token: string): void {
    this.recaptchaToken = token;
    
    if (this.recaptchaToken) {
      // Una vez que tenemos el token, proceder con el envío automáticamente
      // Solo llamar si no estamos ya procesando
      if (!this.isSubmitting && !this.isExecutingRecaptcha) {
      this.onRegistrationSubmit();
      }
    }
  }

  async executeRecaptchaAndSubmit(): Promise<void> {
    // Prevenir ejecuciones múltiples
    if (this.isExecutingRecaptcha || this.isSubmitting) {
      return;
    }
    
    this.isExecutingRecaptcha = true;
    
    try {
    // Si ya tenemos un token válido, proceder directamente
    if (this.recaptchaToken) {
      await this.onRegistrationSubmit();
      return;
    }
    
      if (this.recaptchaComponent) {
        await this.recaptchaComponent.execute();
      } else {
        this.showNotification('Error', 'Error en la verificación de seguridad');
      }
    } catch (error) {
      this.showNotification('Error', 'Error en la verificación de seguridad');
    } finally {
      this.isExecutingRecaptcha = false;
    }
  }

  // Envío del formulario de registro
  async onRegistrationSubmit(): Promise<void> {
    if (this.registrationForm.valid) {
      this.isSubmitting = true;
      
      try {
        // Solo datos básicos del usuario (sin CV)
        const userData = {
          username: this.registrationForm.value.email, // Usar email como username
          email: this.registrationForm.value.email,
          nombre: this.registrationForm.value.nombre,
          apellido: this.registrationForm.value.apellido,
          fullName: `${this.registrationForm.value.nombre} ${this.registrationForm.value.apellido}`,
          telefono: this.registrationForm.value.telefono,
          experiencia: this.registrationForm.value.experiencia,
          expectativaSalarial: this.registrationForm.value.expectativaSalarial,
          modalidadTrabajo: this.registrationForm.value.modalidadTrabajo,
          disponibilidad: this.registrationForm.value.disponibilidad,
          discapacidad: this.registrationForm.value.discapacidad,
          otraDiscapacidad: this.registrationForm.value.otraDiscapacidad,
          comentarios: this.registrationForm.value.comentarios,
          consentimientoDatos: this.registrationForm.value.consentimientoDatos,
          recaptchaToken: this.recaptchaToken
        };

        // Llamada a la API de registro
        const response: any = await this.http.post(`${environment.API_BASE_URL}auth/register`, userData, {
          headers: new HttpHeaders({
            'x-api-key': environment.API_KEY
          })
        }).toPromise();
        
        if (response && response.success) {
          // Avanzar al siguiente paso (confirmación)
          this.currentStep = 2;
          this.scrollToStep(2);
        } else {
          throw new Error(response?.message || 'Error desconocido');
        }
        
      } catch (error: any) {
        console.error('Error en registro:', error);
        
        // Manejar específicamente el error de usuario ya existente
        if (error?.error?.errorCode === 'UsernameExists' || 
            error?.error?.message?.includes('ya existe') ||
            error?.message?.includes('ya existe')) {
          
          this.showNotification(
            'Usuario ya registrado', 
            'Ya tienes una hoja de vida cargada en el sistema. Por favor autentícate para actualizar tus datos.'
          );
          
          // Mostrar opción para ir al login
          this.showLoginOption = true;
          
        } else {
          // Otros errores
        this.analysisError = error instanceof Error ? error.message : 'Error desconocido';
        this.showNotification('Error', 'Hubo un problema al registrar tu cuenta. Por favor intenta nuevamente.');
        }
      } finally {
        this.isSubmitting = false;
      }
    } else {
      // Marcar todos los campos como tocados para mostrar errores visuales
      this.markFormGroupTouched(this.registrationForm);
    }
  }

  // Envío del formulario de confirmación
  async onConfirmationSubmit(): Promise<void> {
    if (this.confirmationForm.valid) {
      this.isConfirming = true;
      
      try {
        const confirmationData = {
          username: this.getEmailValue(), // Usar email como username
          confirmationCode: this.confirmationForm.get('confirmationCode')?.value,
          password: this.confirmationForm.get('password')?.value
          // No reCAPTCHA needed - OTP is sufficient security
        };
        
        // Confirmar cuenta
        const confirmResponse: any = await this.http.post(`${environment.API_BASE_URL}auth/confirm`, confirmationData, {
          headers: new HttpHeaders({
            'x-api-key': environment.API_KEY
          })
        }).toPromise();
        
        if (confirmResponse && confirmResponse.success) {
          // El token ya viene en la respuesta de confirmación
          if (confirmResponse.token) {
            this.authToken = confirmResponse.token;
            this.showNotification('success', '✅ Cuenta confirmada y autenticada exitosamente');
          } else {
            this.showNotification('success', '✅ Cuenta confirmada exitosamente');
          }
          
          // Avanzar al paso 3 (hoja de vida almacenada)
          this.currentStep = 3;
          this.scrollToStep(3);
          
          // Automáticamente continuar al procesamiento después de un breve delay
          setTimeout(() => {
            this.startAutomaticCVProcessing();
          }, 2000);
        } else {
          throw new Error(confirmResponse?.message || 'Error en confirmación');
        }
        
      } catch (error) {
        console.error('Error en confirmación:', error);
        this.showNotification('Error', 'Hubo un problema al confirmar tu cuenta. Por favor intenta nuevamente.');
      } finally {
        this.isConfirming = false;
      }
    }
  }

  // Iniciar procesamiento automático de CV
  private startAutomaticCVProcessing(): void {

    
    // Mostrar indicador de progreso inicial
    this.updateStep('Iniciando procesamiento automático', 5);
    
    // Iniciar WebSocket para actualizaciones en tiempo real
    this.connectWebSocket();
    
    // Subir CV de forma asíncrona (no esperar respuesta)
    this.uploadCVWithAuthAsync();
  }

  private async uploadCVWithAuthAsync(): Promise<void> {
    try {
  
      
      const base64File = await this.fileToBase64(this.selectedFile);
      const formValues = this.registrationForm.value;
      
      const cvData = {
        userId: formValues.email,
        cvFile: base64File,
        fileName: this.selectedFile.name,
        fileType: 'pdf',  // Forzar tipo PDF
        fullName: `${formValues.nombre} ${formValues.apellido}`,
        email: formValues.email,
        phone: formValues.telefono,
        experience: formValues.experiencia,
        modalidadTrabajo: formValues.modalidadTrabajo,
        disability: formValues.discapacidad || '',
        uploadDate: new Date().toISOString()
      };
      
      // Mostrar paso de subida de CV
  
      this.updateStep('Subiendo CV al servidor', 30);
      
      // Enviar CV de forma asíncrona - NO esperar respuesta
      // La lambda continuará procesando y notificará via WebSocket
      this.http.post(`${environment.API_BASE_URL}cv/upload`, cvData, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.authToken}`,
          'x-api-key': environment.API_KEY
        })
      }).subscribe({
        next: (response: any) => {
      
          // No esperar más - la lambda continuará procesando
          // Los WebSockets manejarán las actualizaciones de progreso
        },
        error: (error) => {
          console.error('❌ Error enviando CV:', error);
          
          // Si es un error 504 (Gateway Timeout), NO mostrar error al usuario
          // Los WebSockets seguirán funcionando y el proceso continuará
          if (error?.status === 504) {
            // No cambiar el status ni mostrar notificación
            // El usuario verá el progreso via WebSocket
            return;
          }
          
          // Para otros errores, mostrar notificación normal
          this.currentCVStatus = {
            status: 'failed',
            step: 'Error en la subida del CV',
            progress: 0
          };
          this.showNotification(
            'error',
            'Error enviando CV. Por favor, inténtalo nuevamente.'
          );
        }
      });
      
      // IMPORTANTE: No esperar la respuesta - continuar inmediatamente
      // La lambda procesará el CV y notificará via WebSocket
  
      
    } catch (error) {
      console.error('❌ Error preparando CV:', error);
      this.showNotification(
        'error',
        'Error preparando CV. Por favor, inténtalo nuevamente.'
      );
    }
  }

  /**
   * Trigger background CV processing after successful upload
   */
  private triggerBackgroundProcessing(uploadResponse: any): void {
    try {
  
      
      const processData = {
        userId: uploadResponse.userId,
        processId: uploadResponse.processId,
        s3Key: uploadResponse.s3Key,
        email: uploadResponse.email || this.registrationForm.value.email,
        fullName: uploadResponse.fullName || `${this.registrationForm.value.nombre} ${this.registrationForm.value.apellido}`,
        uploadDate: uploadResponse.uploadDate || new Date().toISOString()
      };
      
      // Call background processing endpoint
      this.http.post(`${environment.API_BASE_URL}cv/process-background`, processData, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'x-api-key': environment.API_KEY
        })
      }).subscribe({
        next: (response: any) => {
      
          this.updateStep('Procesamiento en background iniciado', 55);
        },
        error: (error) => {
          console.warn('⚠️ Background processing trigger failed:', error);
          // Don't fail the entire process - updates will be handled
          this.updateStep('Procesamiento iniciado', 55);
        }
      });
      
    } catch (error) {
      console.error('❌ Error triggering background processing:', error);
      // Don't fail the entire process - updates will be handled
      this.updateStep('Procesamiento iniciado', 55);
    }
  }

  // Actualizar estado de procesamiento
  private updateProcessingStatus(step: string, completed: boolean): void {
    if (!this.cvUploadStatus) {
      this.cvUploadStatus = {
        registration: false,
        upload: false,
        storage: false,
        analysis: false,
        completed: false,
        analysisFailed: false
      };
    }
    
    switch (step) {
      case 'upload':
        this.cvUploadStatus.upload = completed;
        break;
      case 'storage':
        this.cvUploadStatus.storage = completed;
        break;
      case 'analysis':
        this.cvUploadStatus.analysis = completed;
        break;
    }
  }

  // Subir CV con autenticación
  async uploadCVWithAuth(): Promise<void> {
    if (!this.selectedFile || !this.authToken) {
      this.showNotification('Error', 'Archivo CV o token de autenticación faltante');
      return;
    }
    
    this.isUploading = true;
    
    try {
      const base64File = await this.fileToBase64(this.selectedFile);
      
      // Obtener los datos del formulario de registro
      const formValues = this.registrationForm.value;
      
      const cvData = {
        userId: formValues.email, // Usar email como userId
        cvFile: base64File,
        fileName: this.selectedFile.name,
        fileType: 'pdf',  // Forzar tipo PDF
        fullName: `${formValues.nombre} ${formValues.apellido}`,
        email: formValues.email,
        phone: formValues.telefono,
        experience: formValues.experiencia,
        modalidadTrabajo: formValues.modalidadTrabajo,
        disability: formValues.discapacidad || '',
        uploadDate: new Date().toISOString()
      };
      
      // Subir CV con autenticación
      const cvResponse: any = await this.http.post(`${environment.API_BASE_URL}cv/upload`, cvData, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.authToken}`,
          'x-api-key': environment.API_KEY
        })
      }).toPromise();
      
      if (cvResponse && cvResponse.success) {
        this.uploadResult = cvResponse;
        this.registrationSuccess = true;
        
        // Mantener en paso 3 (procesamiento completo)
        this.currentStep = 3;
        this.scrollToStep(3);
        
        // Iniciar WebSocket para actualizaciones en tiempo real
        if (cvResponse.userId) {
          this.connectWebSocket();
        }
        
        this.showNotification('success', '✅ CV subido exitosamente: Tu CV está siendo procesado');
      } else {
        throw new Error(cvResponse?.message || 'Error subiendo CV');
      }
    } catch (error) {
      console.error('Error subiendo CV:', error);
      this.analysisError = error instanceof Error ? error.message : 'Error desconocido';
      this.showNotification('Error', 'Hubo un problema al subir tu CV. Por favor intenta nuevamente.');
    } finally {
      this.isUploading = false;
    }
  }

  // Navegación entre pasos
  goToStep(step: number): void {
    if (step >= 1 && step <= 3) { // Solo tenemos 3 pasos
      this.currentStep = step;
      this.scrollToStep(step);
    }
  }
  
  // Scroll automático al paso actual
  private scrollToStep(step: number): void {
    setTimeout(() => {
      const stepElement = document.querySelector(`.step-${step}`);
      if (stepElement) {
        stepElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  }
  
  // Scroll al inicio de la página
  scrollToTop(): void {
    setTimeout(() => {
      window.scrollTo({ 
        top: 0, 
        behavior: 'smooth' 
      });
    }, 100);
  }
  
  // Ir al login
  goToLogin(): void {
    // Aquí puedes implementar la navegación al login
    // Por favor ocultamos la opción y limpiamos el formulario
    this.showLoginOption = false;
    this.registrationForm.reset();
    this.currentStep = 1;
    
    // Notificación eliminada - no suma valor
    
    // TODO: Implementar navegación real al login
    // this.router.navigate(['/login']);
  }

  // Conectar WebSocket para actualizaciones en tiempo real
  private connectWebSocket(): void {
    if (!this.authToken) {
      return;
    }

    try {
      
      
      this.websocketService.connect(this.registrationForm.value.email, this.authToken)
        .then(() => {
          
          
          // Verificar estado de conexión
          
          
          // Suscribirse a mensajes del WebSocket
          this.websocketSubscription = this.websocketService.messages$.subscribe(
            (messages: WebSocketMessage[]) => {
              
              if (messages.length > 0) {
                const lastMessage = messages[messages.length - 1];
                
                this.handleWebSocketMessage(lastMessage);
              }
            }
          );
          
          // Suscribirse a cambios de estado del WebSocket
          this.websocketService.status$.subscribe(status => {
            
            if (!status.connected && status.error) {
              this.handleWebSocketDisconnection(status.error);
            }
          });
          

        })
        .catch((error) => {
          this.showNotification(
            'warning',
            'No se pudo establecer conexión WebSocket. Tu CV será procesado pero no recibirás actualizaciones en tiempo real.'
          );
        });
      
    } catch (error) {
      this.showNotification(
        'info',
        'Tu CV está siendo procesado. Recibirás actualizaciones en tiempo real cuando esté disponible.'
      );
    }
  }



  // Manejar mensajes del WebSocket
  private handleWebSocketMessage(message: WebSocketMessage): void {
    
    try {
      // Validar estructura del mensaje
      if (!this.validateWebSocketMessage(message)) {
        return;
      }

      // Detectar error específico de Python
      if (message.data && typeof message.data === 'string' && 
          message.data.includes('str object has no attribute setdefault')) {
        this.handlePythonBackendError(message.data);
        return;
      }

      // Detectar otros errores de formato
      if (message.data && typeof message.data === 'string' && 
          (message.data.includes('Error') || message.data.includes('error'))) {
        this.handleBackendError(message.data);
        return;
      }
      
      
      switch (message.type) {
        case 'user_registered':
          
          // Solo actualizar si el progreso actual es menor a 20%
          if (!this.currentCVStatus || this.currentCVStatus.progress < 20) {
            this.updateStep('Verificando información', 20);
          } else {
            
          }
          break;
        case 'cv_uploaded':
          
          this.updateStep('CV procesado', 40);
          break;
        case 'cv_analysis_update':
          
          this.handleCVAnalysisUpdate(message);
          break;
        case 'cv_analysis_complete':
          
          this.handleCVAnalysisComplete(message);
          break;
        case 'cv_analysis_error':
          
          this.handleCVAnalysisError(message);
          break;
        case 'cv_analysis_failed':
          
          this.handleCVAnalysisFailed(message);
          break;
        case 'connection_established':
          
          this.updateStep('Iniciando proceso', 10);
          break;
        case 'pong':
          // Pong messages are just keepalive, no need to show to user
          
          break;
        default:
          
      }
    } catch (error) {
      this.handleWebSocketError(error);
    }
  }

  // Validar estructura de mensaje WebSocket
  private validateWebSocketMessage(message: any): boolean {
    // Verificar estructura básica
    if (!message || typeof message !== 'object') {
      console.error('❌ Mensaje WebSocket inválido - no es un objeto:', message);
      return false;
    }

    // Verificar campos requeridos
    if (!message.type || typeof message.type !== 'string') {
      console.error('❌ Mensaje WebSocket inválido - tipo faltante o inválido:', message);
      return false;
    }

    // Verificar timestamp si existe
    if (message.timestamp && typeof message.timestamp !== 'string') {
      console.error('❌ Mensaje WebSocket inválido - timestamp inválido:', message);
      return false;
    }

    // Verificar que data sea objeto o string válido
    if (message.data !== undefined && 
        typeof message.data !== 'object' && 
        typeof message.data !== 'string') {
      console.error('❌ Mensaje WebSocket inválido - data inválida:', message);
      return false;
    }

    return true;
  }

  // Actualizar paso del proceso
  private updateStep(stepText: string, progress: number): void {
    if (this.currentCVStatus) {
      // Validar que el progreso sea lógico y no retroceda
      const currentProgress = this.currentCVStatus.progress;
      
      // Evitar retrocesos en el progreso (excepto en casos de error o reinicio)
      if (progress < currentProgress && currentProgress > 0 && 
          !stepText.includes('Error') && !stepText.includes('Iniciando')) {
        progress = currentProgress;
      }
      
      // Asegurar que el progreso esté en el rango válido
      progress = Math.max(0, Math.min(100, progress));
      
      // Solo actualizar si hay un cambio real
      if (progress !== currentProgress || stepText !== this.currentCVStatus.step) {
        
        this.currentCVStatus.step = stepText;
        this.currentCVStatus.progress = progress;
        
        // Forzar detección de cambios
        this.cdr.detectChanges();
      }
      
      // Actualizar el estado del proceso
      if (progress < 100) {
        this.currentCVStatus.status = 'processing';
      }
      
      // Reiniciar timeout del proceso
      this.startProcessTimeout();
      
      
    }
  }

  // Iniciar timeout para detectar cuando el proceso se detiene
  private startProcessTimeout(): void {
    // Limpiar timeout anterior si existe
    if (this.processTimeout) {
      clearTimeout(this.processTimeout);
    }
    
    // Solo iniciar timeout si el proceso está activo y no completado
    if (this.currentCVStatus.status === 'processing' && this.currentCVStatus.progress < 100) {
      this.processTimeout = setTimeout(() => {
        this.handleProcessTimeout();
      }, this.PROCESS_TIMEOUT_MS);
    }
  }

  // Manejar timeout del proceso
  private handleProcessTimeout(): void {
    
    const currentProgress = this.currentCVStatus.progress;
    
    // Solo mostrar notificación si el proceso no está completado
    if (this.currentCVStatus.status !== 'completed') {
      // Mostrar mensaje de timeout basado en el progreso actual
      if (currentProgress >= 75) {
        // Análisis estaba en progreso - mostrar progreso gradual
        this.showNotification(
          'info',
          '⏰ El análisis está tomando más tiempo del esperado. Tu CV fue guardado exitosamente y nos pondremos en contacto contigo pronto para revisar tu perfil manualmente.'
        );
        
        // Incrementar progreso gradualmente en lugar de saltar a 100%
        const newProgress = Math.min(95, currentProgress + 5);
        this.updateStep('Proceso completado - Contacto manual pendiente', newProgress);
        
      } else if (currentProgress >= 55) {
        // Perfil guardado pero análisis no iniciado
        this.showNotification(
          'info',
          '⏰ Tu perfil fue guardado exitosamente. El análisis automático no se pudo completar, pero nos pondremos en contacto contigo pronto.'
        );
        
        // Mantener progreso actual en lugar de saltar
        this.updateStep('Perfil guardado - Análisis manual pendiente', currentProgress);
        
      } else {
        // Proceso se detuvo temprano
        this.showNotification(
          'warning',
          '⏰ El proceso se está tomando más tiempo del esperado. Si no recibes actualizaciones pronto, por favor contacta nuestro soporte.'
        );
        
        // Mantener progreso actual
        this.currentCVStatus = {
          status: 'processing',
          step: 'Proceso en progreso - Por favor espera',
          progress: currentProgress
        };
      }
    }
  }

  private handleCVAnalysisUpdate(message: any): void {
    try {
      // Validar que message.data sea un objeto y no un string
      if (typeof message.data === 'string') {
        console.error('❌ Error: message.data es un string en lugar de objeto:', message.data);
        
        // Intentar parsear si es JSON string
        try {
          const parsedData = JSON.parse(message.data);
          message.data = parsedData;
        } catch (parseError) {
          console.error('❌ No se pudo parsear message.data como JSON:', parseError);
          // Manejar como error de análisis
          this.handleCVAnalysisFailed({
            data: {
              finalStatus: 'analysis_failed',
              error: 'Error en el formato de datos del servidor',
              originalData: message.data
            }
          });
          return;
        }
      }

      const step = message.data?.step || 'Analizando CV';
      let progress = message.data?.progress;
      
      // Validar que progress sea un número válido
      if (typeof progress !== 'number' || isNaN(progress) || progress < 0 || progress > 100) {
        console.warn('⚠️ Progress inválido recibido:', progress, 'usando valor por defecto');
        progress = 60; // Valor por defecto para análisis
      }
      
      // Asegurar progreso mínimo y máximo
      progress = Math.max(10, Math.min(95, progress));
      
      // Mostrar notificación para pasos importantes
      if (progress >= 45 && progress <= 50) {
        this.showNotification('info', `📁 ${step}`, 3000);
      } else if (progress >= 60 && progress <= 70) {
        this.showNotification('info', `🧠 ${step}`, 3000);
      } else if (progress >= 80 && progress <= 90) {
        this.showNotification('info', `💾 ${step}`, 3000);
      }
      
      this.updateStep(step, progress);
      
      
    } catch (error) {
      this.handleCVAnalysisFailed({
        data: {
          finalStatus: 'analysis_failed',
          error: 'Error procesando actualización del análisis',
          originalError: error.message
        }
      });
    }
  }

  private handleCVAnalysisComplete(message: any): void {
    
    // Detener ping/pong del WebSocket
    this.stopWebSocketPingPong();
    
    // Actualizar estado del análisis
    this.analysisCompleted = true;
    this.analysisResult = message.data?.analysisResult || message.data;
    
    // También actualizar uploadResult para compatibilidad con el HTML
    this.uploadResult = {
      success: true,
      message: 'CV uploaded and analyzed successfully',
      userId: message.data?.userId || this.registrationForm.value?.email,
      analysisResult: this.analysisResult,
      status: 'completed'
    };
    
    // Determinar si el análisis falló
    if (message.data?.analysisResult?.error || message.data?.estado_cv === 'Error') {
      this.analysisFailed = true;
      
    } else {
      this.analysisFailed = false;
      
    }
    
    // Actualizar el estado del CV
    if (this.currentCVStatus) {
      this.currentCVStatus.status = 'completed';
      this.currentCVStatus.progress = 100;
      this.currentCVStatus.step = 'Proceso completado';
    }
    
    // Mostrar notificación de éxito con más detalles
    const finalMessage = this.analysisFailed 
              ? '⚠️ Proceso completado con advertencias. Tu CV fue procesado pero el análisis automático encontró algunos problemas.'
        : '✅ Proceso completado exitosamente: Tu CV ha sido procesado completamente.';
    
    this.showNotification('success', finalMessage, 5000);
    
    // Log del resultado final
    
    
    // Log específico de los campos clave para debugging
    
    
    // Verificar que uploadResult esté correctamente inicializado
    
    
    // Forzar detección de cambios si es necesario
    setTimeout(() => {
      
      
      // Forzar detección de cambios
      this.cdr.detectChanges();
    }, 100);
  }

  private handleCVAnalysisError(message: any): void {
    
    this.handleCVAnalysisFailed({
      data: {
        finalStatus: 'analysis_error',
        error: message.data?.error || 'Error desconocido en el análisis',
        originalError: message.data?.originalError || message.data?.error
      }
    });
  }

  private handleCVAnalysisFailed(message: any): void {
    try {
      // Detectar error específico de Python
      let errorMessage = message.data?.error || 'Error desconocido en el análisis';
      
      if (typeof errorMessage === 'string' && 
          errorMessage.includes('str object has no attribute setdefault')) {
        console.error('🐍 Error específico de Python detectado en análisis fallido:', errorMessage);
        this.handlePythonBackendError(errorMessage);
        return;
      }
      
      const finalStatus = message.data?.finalStatus;
      
      if (finalStatus === 'analysis_failed') {
        this.showNotification(
          'warning',
          '⚠️ Tu hoja de vida fue guardada exitosamente. El análisis automático no pudo completarse, pero nos pondremos en contacto contigo para revisar tu perfil manualmente. Tu información está segura en nuestro sistema.'
        );
      } else {
        this.showNotification(
          'warning',
          '⚠️ Tu hoja de vida fue guardada y nos pondremos en contacto contigo. No fue posible completar el análisis automático. Por favor, revisa si el documento que cargaste estaba correcto.'
        );
      }
      
      // Actualizar UI para mostrar estado fallido
      this.currentCVStatus = {
        status: 'failed',
        step: 'Análisis fallido',
        progress: 50 // Mantener progreso parcial en lugar de resetear a 0
      };
      
      // Mostrar mensaje específico para el usuario
      this.showFinalStatusMessage('Tu CV fue guardado exitosamente. El análisis automático no pudo completarse, pero nos pondremos en contacto contigo pronto.');
      
    } catch (error) {
      console.error('❌ Error procesando fallo de análisis:', error);
      // Fallback a manejo básico
      this.currentCVStatus = {
        status: 'failed',
        step: 'Error en análisis',
        progress: 50
      };
    }
  }

  // Manejar desconexión del WebSocket
  private handleWebSocketDisconnection(error: string): void {
    
    // Mostrar estado final basado en el progreso actual
    if (this.currentCVStatus.progress >= 90) {
      this.showNotification(
        'success',
        'Proceso completado exitosamente. Tu CV ha sido procesado.'
      );
    } else if (this.currentCVStatus.progress >= 60) {
      this.showNotification(
        'info',
        'Tu CV está siendo procesado. El análisis está en progreso y se completará pronto.'
      );
    } else if (this.currentCVStatus.progress >= 40) {
      this.showNotification(
        'info',
        'Tu CV ha sido cargado exitosamente. El análisis comenzará automáticamente.'
      );
    } else {
      this.showNotification(
        'info',
        'Tu CV está siendo procesado. Recibirás actualizaciones cuando esté disponible.'
      );
    }
    
    // Actualizar estado final
    this.currentCVStatus = {
      status: 'processing',
      step: 'Proceso en progreso',
      progress: this.currentCVStatus.progress
    };
  }

  private showFinalStatusMessage(message: string): void {
    // Crear un elemento de notificación persistente
    const statusDiv = document.createElement('div');
    statusDiv.className = 'final-status-message';
    statusDiv.innerHTML = `
      <div class="status-content">
        <h3>✅ Proceso Completado</h3>
        <p>${message}</p>
        <p><strong>Estado:</strong> Tu información está guardada en nuestro sistema.</p>
        <p><strong>Próximo paso:</strong> Nos pondremos en contacto contigo pronto.</p>
      </div>
    `;
  }

  // Detener el ping/pong del WebSocket después de completar
  private stopWebSocketPingPong(): void {
    
    this.websocketService.stopPingPong();
  }

  // Mostrar el resultado del análisis inteligente
  private showAnalysisResult(analysisResult: any): void {
    // Verificar si el análisis falló
    if (analysisResult.error || analysisResult.estado_cv === 'Error') {
      // Mostrar mensaje simple cuando el análisis falla
      this.showNotification('success', '✅ Proceso completado: Tu CV ha sido guardado exitosamente. Te contactaremos pronto con oportunidades laborales.', 10000);
    } else {
      // Mostrar resultado exitoso del análisis con la nueva estructura de Bedrock
      const resultMessage = `
        <div class="analysis-result">
          <h4>🎯 Resultado del Análisis Inteligente</h4>
          <p><strong>Perfil detectado:</strong> ${analysisResult.perfil_profesional || 'No especificado'}</p>
          <p><strong>Experiencia:</strong> ${analysisResult.experiencia_laboral?.anos_experiencia || 'No especificada'}</p>
          <p><strong>Habilidades principales:</strong> ${analysisResult.habilidades_principales?.tecnologias?.join(', ') || 'No especificadas'}</p>
          <p><strong>Puntuación:</strong> ⭐ ${analysisResult.puntuacion_general || 0}/10</p>
          <p><strong>Estado del CV:</strong> ${analysisResult.estado_cv || 'No evaluado'}</p>
          <p><strong>Estado:</strong> ✅ Análisis completado exitosamente</p>
          <p><strong>Próximo paso:</strong> 🚀 Te contactaremos pronto con oportunidades laborales</p>
        </div>
      `;
      
      this.showNotification('success', resultMessage, 10000); // Mostrar por 10 segundos
    }
  }

  // Reenvío de código de confirmación
  async resendConfirmationCode(): Promise<void> {
    this.isResending = true;
    
    try {
      // Aquí iría la llamada a la API para reenviar el código
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      this.showNotification('success', '📧 Código reenviado: Se ha enviado un nuevo código de confirmación a tu email');
    } catch (error) {
      
      this.showNotification('Error', 'No se pudo reenviar el código. Por favor intenta nuevamente.');
    } finally {
      this.isResending = false;
    }
  }

  // El estado del CV se actualiza automáticamente via WebSocket
  // No se requiere polling manual

  // Utilidades
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result as string;
        // Remover el prefijo data:application/pdf;base64,
        const base64Clean = base64.split(',')[1];
        resolve(base64Clean);
      };
      reader.onerror = error => reject(error);
    });
  }

  private uploadCV(formData: any) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': environment.API_KEY || ''
    });
    
    return this.http.post<CVUploadResponse>(
      `${environment.API_BASE_URL}cv/upload`,
      formData,
      { headers }
    );
  }

  // Validación de tipo de archivo - SOLO PDF
  private isValidFileType(file: File): boolean {
    return file.type === 'application/pdf';
  }

  // Métodos para las tarjetas de oportunidades
  aplicarOferta(oferta: any): void {
    this.showNotification('info', '🚀 Próximamente estarán habilitadas estas capacidades');
  }
  
  // Métodos para las rutas de formación
  inscribirseRuta(ruta: any): void {
    this.showNotification('info', '🚀 Próximamente estarán habilitadas');
  }
  
  // Método para expandir/contraer FAQ
  toggleFaq(index: number): void {
    this.faq[index].expanded = !this.faq[index].expanded;
  }

  // Notificaciones
  private showNotification(title: string, message: string, duration: number = 5000): void {
    // Crear elemento de notificación
    const notification = document.createElement('div');
    notification.className = `notification notification-${title}`;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${title === 'success' ? '#d4edda' : title === 'error' ? '#f8d7da' : '#d1ecf1'};
      color: ${title === 'success' ? '#155724' : title === 'error' ? '#721c24' : '#0c5460'};
      border: 1px solid ${title === 'success' ? '#c3e6cb' : title === 'error' ? '#f5c6cb' : '#bee5eb'};
      border-radius: 8px;
      padding: 16px;
      margin: 8px;
      max-width: 400px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      font-family: Arial, sans-serif;
      font-size: 14px;
      line-height: 1.4;
    `;

    // Si el mensaje contiene HTML, usarlo directamente
    if (message.includes('<div') || message.includes('<h4') || message.includes('<p>')) {
      notification.innerHTML = message;
    } else {
      // Si es texto plano, crear estructura básica
      notification.innerHTML = `
        <div style="display: flex; align-items: flex-start; gap: 12px;">
          <div style="flex: 1;">
            <h4 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600;">${title}</h4>
            <p style="margin: 0; white-space: pre-wrap;">${message}</p>
          </div>
          <button onclick="this.parentElement.parentElement.remove()" style="
            background: none;
            border: none;
            font-size: 18px;
            cursor: pointer;
            color: inherit;
            opacity: 0.7;
            padding: 0;
            margin: 0;
          ">&times;</button>
        </div>
      `;
    }

    // Agregar botón de cerrar si no está presente
    if (!notification.querySelector('button')) {
      const closeButton = document.createElement('button');
      closeButton.innerHTML = '&times;';
      closeButton.style.cssText = `
        position: absolute;
        top: 8px;
        right: 8px;
        background: none;
        border: none;
        font-size: 18px;
        cursor: pointer;
        color: inherit;
        opacity: 0.7;
        padding: 0;
        margin: 0;
      `;
      closeButton.onclick = () => notification.remove();
      notification.appendChild(closeButton);
    }

    // Agregar al DOM
    document.body.appendChild(notification);

    // Auto-remover después del tiempo especificado
    if (duration > 0) {
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
        }
      }, duration);
    }

    
  }



  // Limpieza de WebSocket
  private cleanupWebSocket(): void {
    if (this.websocketSubscription) {
      this.websocketSubscription.unsubscribe();
      this.websocketSubscription = null;
    }
  }

  // Navegación después del análisis
  goToNextStep(): void {
    
    // Aquí puedes implementar la lógica para ir al siguiente paso
    // Por ejemplo, ir a una página de oportunidades, dashboard, etc.
    
    // Por ahora, solo mostramos un mensaje
    this.showNotification('info', '🚀 Próximamente: Aquí verás las oportunidades laborales disponibles para tu perfil.', 8000);
    
    // También puedes resetear el estado si es necesario
    // this.analysisCompleted = false;
    // this.analysisFailed = false;
    // this.analysisResult = null;
  }

  // Helper para marcar todos los campos como tocados
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Manejar error específico de Python backend
  private handlePythonBackendError(errorMessage: string): void {
    
    // Mostrar mensaje específico para el usuario
    this.showNotification(
      'error',
      '⚠️ Error en el análisis del CV: El servidor encontró un problema interno. Tu CV fue guardado exitosamente y nos pondremos en contacto contigo pronto para revisar tu perfil manualmente.'
    );
    
    // Actualizar estado para mostrar que el proceso falló pero el CV está guardado
    if (this.currentCVStatus) {
      this.currentCVStatus.status = 'failed';
      this.currentCVStatus.step = 'Error en análisis - CV guardado';
      this.currentCVStatus.progress = 50; // Mantener progreso parcial
    }
    
    // Detener el proceso de análisis
    this.stopWebSocketPingPong();
  }

  // Manejar errores generales del backend
  private handleBackendError(errorMessage: string): void {
    
    // Mostrar mensaje genérico pero informativo
    this.showNotification(
      'warning',
      '⚠️ Error en el análisis del CV: El servidor encontró un problema interno. Tu CV fue guardado exitosamente y nos pondremos en contacto contigo pronto para revisar tu perfil manualmente.'
    );
    
    // Actualizar estado
    if (this.currentCVStatus) {
      this.currentCVStatus.status = 'failed';
      this.currentCVStatus.step = 'Error del servidor - CV guardado';
      this.currentCVStatus.progress = 50;
    }
    
    // Detener el proceso
    this.stopWebSocketPingPong();
  }

  // Manejar errores generales del WebSocket
  private handleWebSocketError(error: any): void {
    
    // Mostrar mensaje de error de conexión
    this.showNotification(
      'warning',
      '⚠️ Error de conexión: Se perdió la conexión con el servidor. Tu CV fue guardado exitosamente y nos pondremos en contacto contigo pronto.'
    );
    
    // Actualizar estado
    if (this.currentCVStatus) {
      this.currentCVStatus.status = 'failed';
      this.currentCVStatus.step = 'Error de conexión - CV guardado';
      this.currentCVStatus.progress = 50;
    }
    
    // Detener el proceso
    this.stopWebSocketPingPong();
  }

  // Getter para obtener el resultado del análisis de forma consistente
  get currentAnalysisResult(): any {
    return this.uploadResult?.analysisResult || this.analysisResult;
  }

  // Getter para verificar si hay resultados disponibles
  get hasAnalysisResults(): boolean {
    return !!(this.uploadResult?.analysisResult || this.analysisResult);
  }
}
